import {
  createBaseThemeOptions,
  createUnifiedTheme,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  defaultTypography,
} from '@backstage/theme';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#172B4D',
  };
});

const updatedPalette = {
  ...palettes.light,
  primary: {
    main: '#0b7469', // '#0c3172', // main color
    light: '#4C9AFF', // blue
    dark: '#052556', // dark blue
  },
  secondary: {
    main: '#7a0d72', // selection color
    light: '#FFAB00', // orange-yellow
    dark: '#6554C0', // purple
  },
  grey: {
    50: '#f5f5f5',
    100: '#e0e0e0',
    200: '#bdbdbd',
    300: '#9e9e9e',
    400: '#757575',
    500: '#616161',
    600: '#424242',
    700: '#303030',
    800: '#212121',
    900: '#121212',
  },
  error: {
    main: '#FF5630', // red
    light: '#FF8F73',
    dark: '#DE350B',
  },
  warning: {
    main: '#FFAB00', // orange
    light: '#FFE380',
    dark: '#FF8B00',
  },
  info: {
    main: '#0b7469', // blue
    light: '#4C9AFF',
    dark: '#0747A6',
  },
  success: {
    main: '#36B37E', // green
    light: '#79F2C0',
    dark: '#006644',
  },
  background: {
    default: '#dee6eb',
    paper: '#f5f5f5',
    // default: '#f5f5f5', // light grey
    // paper: '#dee6eb', // '#ffffff', // white
  },
  text: {
    primary: '#0c3172', // main text color dark blue
    secondary: '#03529a', //secondary text color  medium blue 
  },
  qmul: {
    white: '#ffffff', // white text color
    grey: '#f6f6f6', // grey text color
    pink: '#dc2692', // pink text color
    purple: '#7a0d72', // purple text color
    blue: '#0c3172', // blue text color
    darkblue: '#052556', // dark blue text color
    green: '#0b7469', // green text color
    yellow: '#cea600', // yellow text color
    lightblue: '#bdd2e5', // light blue text color
    lightpink: '#f7b4f2', // light pink
    black: '#000000', // black
    darkgrey: '#393f49', // dark grey
    argoblue: '#dee6eb', // very light blue
  },
  navigation: {
    ...palettes.light.navigation,
    background: '#0c3172', // side panel background
    indicator: '#cea600', // side panel left line
    color: '#ffffff', // side panel text
    selectedColor: '#cea600', // '#cea500', // selection
    navItem: {
      hoverBackground: 'rgba(252,219,119,0.5)',
    },
  },
};

const updatedTypography = {
  ...defaultTypography,
  htmlFontSize: 16,
  fontFamily: 'Roboto, sans-serif',
  h1: {
    fontSize: 54,
    fontWeight: 700,
    marginBottom: 10,
    color: '#0c3172',
  },
  h2: {
    fontSize: 40,
    fontWeight: 700,
    marginBottom: 8,
    color: '#0c3172',
  },
  h3: {
    fontSize: 32,
    fontWeight: 500,
    marginBottom: 6,
    color: '#0c3172',
  },
  h4: {
    fontWeight: 700,
    fontSize: 28,
    marginBottom: 6,
    color: '#0c3172',
  },
  h5: {
    fontWeight: 700,
    fontSize: 24,
    marginBottom: 4,
    color: '#0c3172',
  },
  h6: {
    fontWeight: 700,
    fontSize: 20,
    marginBottom: 2,
    color: '#0c3172',
  },
  body1: {
    color: '#0c3172',
  },
  body2: {
    color: '#03529a',
  },
  subtitle1: {
    color: '#0c3172',
  },
  subtitle2: {
    color: '#03529a',
  },
  button: {
    color: '#ffffff',
  },
  caption: {
    color: '#f7b4f2', // '#ffffff',
  },
  overline: {
    color: '#0c3172',
  },
};

export const qmuTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: updatedPalette,
    typography: updatedTypography,
  }),
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  fontFamily: 'Roboto',
  components: {
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }) => ({
          backgroundImage: 'none', // 'unset',
          backgroundColor: updatedPalette.primary.dark,
          paddingBottom: theme.spacing(1),
        }),
        title: () => ({
          color: updatedPalette.qmul.white,
          fontWeight: 900,
        }),
        subtitle: () => ({
          color: updatedPalette.qmul.grey,
        }),
        type: () => ({
          color: updatedPalette.qmul.grey,
        }),
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontSize: 'inherit',
        },
      },
    },
    BackstageOpenedDropdown: {
      styleOverrides: {
        icon: {
          '& path': {
            fill: updatedPalette.text.primary,
          },
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '&> :first-child': {
            borderRadius: '2px',
            borderBottom: `2px solid ${updatedPalette.grey[100]}`,
            color: updatedPalette.text.primary,
            backgroundColor: updatedPalette.background.paper,
          },
          '& th': {
            borderTop: 'none',
            textTransform: 'none !important',
            backgroundColor: updatedPalette.grey[50],
            color: updatedPalette.text.primary,
          },
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.primary.dark,
        },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          color: updatedPalette.qmul.white,
          backgroundColor: updatedPalette.qmul.darkblue,
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.white,
        },
        label: {
          color: updatedPalette.qmul.white,
        },
        value: {
          color: updatedPalette.qmul.white,
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.pink,
          backgroundColor: updatedPalette.qmul.darkblue,
          backgroundImage: 'none',
        },
      },
    },
    BackstageAvatar: {
      styleOverrides: {
        avatar: {
          visibility: 'hidden',
        },
      },
    },
    // CatalogReactUserListPicker: {
    //   styleOverrides: {
    //     root: {
    //       // color: updatedPalette.text.primary,
    //       borderRadius: '2px',
    //       backgroundColor: updatedPalette.grey[400],
    //       margin: '10px',
    //     },
    //   },
    // },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        standardError: () => ({
          color: updatedPalette.text.primary,
          backgroundColor: updatedPalette.error.light,
          '& $icon': {
            color: updatedPalette.text.primary,
          },
        }),
        standardInfo: () => ({
          color: updatedPalette.text.primary,
          backgroundColor: updatedPalette.info.light,
          '& $icon': {
            color: updatedPalette.text.primary,
          },
        }),
        standardSuccess: () => ({
          color: updatedPalette.text.primary,
          backgroundColor: updatedPalette.success.light,
          '& $icon': {
            color: updatedPalette.text.primary,
          },
        }),
        standardWarning: () => ({
          color: updatedPalette.text.primary,
          backgroundColor: updatedPalette.warning.light,
          '& $icon': {
            color: updatedPalette.text.primary,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&[aria-expanded=true]': {
            color: updatedPalette.text.primary,
          },
          '&[aria-expanded=true] path': {
            fill: updatedPalette.text.primary,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9,30,69,0.54)', // transparent blue
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          textTransform: 'none',
          color: updatedPalette.qmul.blue,
          fontWeight: 700,
          '&:hover': {
            color: updatedPalette.qmul.white,
            backgroundColor: updatedPalette.qmul.yellow,
          },
          '&.Mui-disabled': {
            backgroundColor: updatedPalette.qmul.grey,
          },
        },
        contained: {
          boxShadow: 'none',
          color: updatedPalette.qmul.blue,
        },
        containedPrimary: {
          backgroundColor: updatedPalette.qmul.blue,
          color: updatedPalette.qmul.white,
          '&:hover': {
            backgroundColor: updatedPalette.qmul.yellow,
          },
        },
        textPrimary: {
          color: updatedPalette.qmul.white,
          background: updatedPalette.qmul.blue,
          '&:hover': {
            color: updatedPalette.qmul.white,
            backgroundColor: updatedPalette.qmul.yellow,
          },
        },
        text: {
          color: updatedPalette.qmul.darkblue, // updatedPalette.qmul.white,
          backgroundColor: updatedPalette.qmul.grey, // updatedPalette.qmul.blue,
          '&:hover': {
            color: updatedPalette.qmul.white,
            backgroundColor: updatedPalette.qmul.yellow
          },
        },
        outlinedPrimary: {
          color: updatedPalette.qmul.white,
          borderColor: updatedPalette.qmul.blue,
          background: updatedPalette.qmul.blue,
          '&:hover': {
            color: updatedPalette.qmul.white,
            borderColor: updatedPalette.qmul.yellow,
            backgroundColor: updatedPalette.qmul.yellow,
          },
        },
        startIcon: {
          paddingLeft: '8px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          backgroundColor: updatedPalette.grey[50],
          color: updatedPalette.secondary.dark,
          margin: 4,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&[aria-expanded]': {
            backgroundColor: updatedPalette.background.paper,
            color: updatedPalette.text.primary,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 10,
          color: updatedPalette.primary.main,
        },
        switchBase: {
          padding: 12,
        },
        thumb: {
          backgroundColor: updatedPalette.warning.main,
          height: 14,
          width: 14,
        },
        track: {
          borderRadius: 9,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          color: updatedPalette.text.primary,
          // backgroundColor: updatedPalette.qmul.grey,
          // borderRadius: '5px',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          // color: updatedPalette.text.primary,
          // backgroundColor: updatedPalette.qmul.grey,
          borderRadius: '5px',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.darkblue,
        },
        button: {
          color: updatedPalette.text.primary,
        },
        body1: {
          color: updatedPalette.qmul.darkblue,
        },
        body2: {
          color: updatedPalette.text.secondary,
        },
        caption: {
          color: updatedPalette.qmul.yellow,
        },
        h1: {
          color: updatedPalette.primary.main,
        },
        h2: {
          color: updatedPalette.primary.main,
        },
        h3: {
          color: updatedPalette.primary.main,
        },
        h4: {
          color: updatedPalette.qmul.blue,
        },
        h5: {
          color: updatedPalette.qmul.white,
          borderRadius: '5px',
          backgroundColor: updatedPalette.qmul.darkblue,
          padding: '10px',
        },
        h6: {
          color: updatedPalette.qmul.darkblue,
        },
        overline: {
          color: updatedPalette.primary.main,
        },
        subtitle1: {
          color: updatedPalette.qmul.purple, // updatedPalette.secondary.main,
        },
        subtitle2: {
          color: updatedPalette.qmul.white,
        },
        // colorTextSecondary: {
        //   color: updatedPalette.qmul.green, // notification text
        // },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.grey,
          // margin: '5px',
          borderRadius: '5px',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.white,
          backgroundColor: updatedPalette.qmul.darkblue,
        },
        title: {
          color: updatedPalette.qmul.white,
        },
        // Without this, MUI renders the subheader with `colorTextSecondary`,
        // i.e. palette.text.secondary (#03529a, a mid blue) — nearly illegible
        // on the darkblue CardHeader background above. Matches the page header
        // subtitle so a template's description reads the same in both places.
        subheader: {
          color: updatedPalette.qmul.grey,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.blue,
          backgroundColor: updatedPalette.qmul.grey,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.primary.main,
          borderRadius: '5px',
          color: updatedPalette.qmul.black,
        },
        regular: {
          backgroundColor: updatedPalette.qmul.grey,
          borderRadius: '5px',
        },
        gutters: {
          backgroundColor: updatedPalette.primary.main,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          color: updatedPalette.text.primary,
          // paddingLeft: '10px',
          // paddingRight: '10px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.pink,
          // backgroundColor: updatedPalette.qmul.darkblue,
          borderRadius: '2px',
          padding: '1em',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.white,
          //borderRadius: '3px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.white,
          border: `2px solid ${updatedPalette.qmul.grey}`,
          borderRadius: '4px',
          // padding: '5px',
        },
        input: {
          color: updatedPalette.qmul.darkblue,
          border: 0,
          background: 'none',
          borderRadius: '2px',
          // marginLeft: '10px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.darkblue,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.darkblue,
          backgroundColor: updatedPalette.qmul.white,
          borderRadius: '3px',
        },
        colorPrimary: updatedPalette.warning.main,
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.yellow,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          color: updatedPalette.text.primary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.pink,
        },
        underlineNone: {
          textDecoration: 'none', // ????
        }
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.blue,
        },
        colorDefault: {
          backgroundColor: updatedPalette.qmul.white,
          border: `1px solid ${updatedPalette.qmul.blue}`,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.blue,
          // '&.Mui-disabled': {
          //   backgroundColor: updatedPalette.qmul.white,
          // },
        },

      },
    },
    MuiInputLabel: {
      styleOverrides: {
        formControl: {
          top: 0,
          left: 0,
          position: 'absolute',
          transform: 'none', // Remove the transform field
          padding: '3px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: updatedPalette.qmul.green, // updatedPalette.qmul.pink,
          backgroundColor: updatedPalette.qmul.white,
          margin: '10px',
          // marginRight: '10px',
          padding: '10px',
        },
        rounded: {
          // borderRadius: '5px',
          backgroundColor: updatedPalette.qmul.grey,
          paddingRight: '10px',
        },
        elevation1: { 
          margin: '10px',
        }
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.grey,
          borderRadius: '2px',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
          },
        },
        label: {
          color: updatedPalette.qmul.blue,
          backgroundColor: 'inherit',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          // backgroundColor: updatedPalette.qmul.grey, // use this
          // backgroundColor: updatedPalette.background.default,
          // borderRadius: '4px',
        },
        item: {
          // backgroundColor: updatedPalette.qmul.grey,
          borderRadius: '2px',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          // backgroundColor: updatedPalette.qmul.white,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: updatedPalette.qmul.grey,
          },
        },
      },
    },
    // MuiListItemIcon: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: updatedPalette.qmul.white,
    //     },
    //   },
    // },
    MuiCollapse: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.grey,
          // borderRadius: '2px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: updatedPalette.qmul.argoblue,
          // borderRadius: '2px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: updatedPalette.qmul.argoblue,
        },
      },
    },
    // MuiBox: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: updatedPalette.qmul.grey,
    //     },
    //   },
    // }
    // MuiCssBaseline: {
    //   styleOverrides: {
    //     body: {
    //       backgroundColor: updatedPalette.qmul.white,
    //     },
    //   },
    // },
  },
}); // Corrected closing brace and semicolon
